import { difference, pluck, uniq } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComboboxOption } from '@library/components/selects';
import type { FormattedGameMode } from '@library/types/gameModes';
import type { LeagueInfo } from '@library/types/leagues';
import type { RankInfo } from '@library/types/boardBuilder';
import { useBoardBuilderPlayers } from '@bo-hooks/boardBuilder/useBoadBuilderPlayers';
import { useGameModes } from '@bo-hooks/gameModes/useGameModes';
import { useLeagueDurationAttributes } from '@bo-hooks/useLeagueDurationAttributes';

export const useBoardBuilderFilters = ({
  initialDurationId,
  leagueInfo
}: {
  initialDurationId: string;
  leagueInfo: LeagueInfo;
}) => {
  const {
    confirmedPlayersList,
    handleCancelSelectedPlayersList,
    handleConfirmSelectedPlayersList,
    handleResetPlayers,
    handleSelectPlayer,
    handleUnSelectPlayer,
    newlySelectedPlayersList,
    selectedPlayersList,
    unSelectedPlayersList
  } = useBoardBuilderPlayers();

  const { gameModesData: gameModes, isLoadingGameModes } = useGameModes({});

  const [selectedStatTypes, setSelectedStatTypes] = useState<Array<string>>([]);
  const [confirmedStatTypes, setConfirmedStatTypes] = useState<Array<string>>(
    []
  );
  const [selectedGameModes, setSelectedGameModes] = useState<
    Array<FormattedGameMode>
  >([]);
  const [confirmedGameModes, setConfirmedGameModes] = useState<
    Array<FormattedGameMode>
  >([]);

  const [selectedDurationId, setSelectedDurationId] =
    useState(initialDurationId);

  useEffect(() => {
    if (isLoadingGameModes) {
      return;
    }
    setSelectedGameModes(gameModes);
  }, [gameModes, isLoadingGameModes]);

  const intialRankInfo: RankInfo = { rank: undefined, populateRank: false };

  const [rankInfo, setRankInfo] = useState<RankInfo>(intialRankInfo);

  const handleResetFilters = useCallback(() => {
    setSelectedStatTypes([]);
    setConfirmedStatTypes([]);
    setSelectedGameModes(
      gameModes.filter(({ name }) => !name.toLowerCase().includes('culture'))
    );
    setConfirmedGameModes(
      gameModes.filter(({ name }) => !name.toLowerCase().includes('culture'))
    );
    handleResetPlayers();
  }, [handleResetPlayers, gameModes]);

  const handleDurationChange = (value: string) => {
    setSelectedDurationId(value);
  };

  const handleSelectStatTypes = (
    selectStatTypes: Array<ComboboxOption> | null
  ) => {
    const selectedStatTypeIds: Array<string> = pluck(
      'value',
      selectStatTypes ?? []
    ) as Array<string>;

    setSelectedStatTypes(selectedStatTypeIds);
  };

  const handleConfirmStatTypes = () => {
    setConfirmedStatTypes(selectedStatTypes);
  };

  const handleConfirmGameModes = () => {
    setConfirmedGameModes(selectedGameModes);
  };

  const handleConfirmSelectChanges = () => {
    handleConfirmStatTypes();
    handleConfirmSelectedPlayersList();
    handleConfirmGameModes();
  };

  const handleCancelSelectChanges = () => {
    setSelectedStatTypes(confirmedStatTypes);
    setSelectedGameModes(confirmedGameModes);
    handleCancelSelectedPlayersList();
  };

  const handlePopulateRankInfo = (startRank: number) => {
    setRankInfo({ populateRank: true, rank: startRank });
  };

  const handleResetRankInfo = () => {
    setRankInfo(intialRankInfo);
  };

  const handleGameModesChange = (
    selectedValues: Array<ComboboxOption> | null,
    isFullDuration: boolean
  ) => {
    const newGameModes = gameModes.filter(
      (gameMode) =>
        selectedValues?.some(({ label }) => label === gameMode.label) &&
        // Remove Squads game mode for sub-durations
        (isFullDuration || !String(gameMode.label).includes('Squads'))
    );
    setSelectedGameModes(newGameModes);
  };

  // Reset Selected/Confirmed Players and Stat Types once the league changes
  useEffect(() => {
    if (leagueInfo) {
      handleResetFilters();
    }
  }, [handleResetFilters, leagueInfo]);

  const selectedLeagueDurationAttributes = useLeagueDurationAttributes({
    leagueInfo,
    durationId: selectedDurationId
  });

  const unSelectedStatTypes = useMemo(
    () => difference(confirmedStatTypes, selectedStatTypes),
    [confirmedStatTypes, selectedStatTypes]
  );

  const newlySelectedStatTypes = useMemo(
    () => difference(selectedStatTypes, confirmedStatTypes),
    [confirmedStatTypes, selectedStatTypes]
  );

  const statTypes = useMemo(
    () =>
      uniq(leagueInfo?.baseLeagueFilters.flatMap((league) => league.statTypes)),
    [leagueInfo]
  );

  const unSelectedGameModes = useMemo(
    () => difference(confirmedGameModes, selectedGameModes),
    [confirmedGameModes, selectedGameModes]
  );

  const newlySelectedGameModes = useMemo(
    () => difference(selectedGameModes, confirmedGameModes),
    [confirmedGameModes, selectedGameModes]
  );

  const hasSelections =
    selectedStatTypes.length > 0 ||
    selectedPlayersList.length > 0 ||
    selectedGameModes.length !== gameModes.length;

  const hasSelectionChanged =
    difference(confirmedStatTypes, selectedStatTypes).length > 0 ||
    difference(selectedStatTypes, confirmedStatTypes).length > 0 ||
    difference(selectedPlayersList, confirmedPlayersList).length > 0 ||
    difference(confirmedPlayersList, selectedPlayersList).length > 0 ||
    difference(selectedGameModes, confirmedGameModes).length > 0 ||
    difference(confirmedGameModes, selectedGameModes).length > 0;

  return {
    confirmedGameModes,
    confirmedPlayersList,
    confirmedStatTypes,
    gameModes,
    handleDurationChange,
    handleCancelSelectChanges,
    handleConfirmSelectChanges,
    handleGameModesChange,
    handlePopulateRankInfo,
    handleResetFilters,
    handleResetRankInfo,
    hasSelectionChanged,
    hasSelections,
    handleSelectPlayer,
    handleSelectStatTypes,
    handleUnSelectPlayer,
    newlySelectedGameModes,
    newlySelectedPlayersList,
    newlySelectedStatTypes,
    rankInfo,
    selectedDurationId,
    selectedGameModes,
    selectedPlayersList,
    selectedStatTypes,
    selectedLeagueDurationAttributes,
    statTypes,
    unSelectedGameModes,
    unSelectedPlayersList,
    unSelectedStatTypes
  };
};
